import { countryNameToIso2 } from '../lib/cds/countries'

export interface InvoiceItem {
  line_no: number
  hs_code: string | null
  hs_code_suggestion: string | null
  hs_code_confidence: number
  goods_description: string
  pcs: number | null
  ctns: number | null
  gross_weight: number | null
  net_weight: number | null
  amount: number | null
  currency: string | null
  country_of_origin: string | null
  // K10: per-line CBM/volume (m3) — null, not 0, when the source document has no CBM
  // column at all, so callers can tell "not captured" apart from "genuinely zero".
  cbm: number | null
  unit_value: number | null
  // Z98: set when CTNS was auto-adjusted (parsed 0 -> 1); holds the original value. Cleared on manual edit.
  ctns_original?: number | null
}

export interface InvoicePivotItem {
  hs_code: string
  goods_description: string
  pcs: number
  ctns: number
  gross_weight: number
  net_weight: number
  amount: number
  cbm: number
}

export interface InvoiceHeader {
  invoice_no: string | null
  job_ref: string | null
  bill_no: string | null
  declare_method: string | null
  trade_term: string | null
  shipper: string | null
  shipper_address: string | null
  consignee: string | null
  consignee_address: string | null
  country_of_origin: string | null
  destination_country: string | null
  invoice_date: string | null
  currency: string
  total_amount: number | null
  gross_weight_kg: number | null
  net_weight_kg: number | null
  packages: number | null
  // K10: header/total-row CBM (m3), when the document has one — null if not captured.
  total_cbm: number | null
  incoterms: string | null
  vessel_flight: string | null
  port_of_loading: string | null
  port_of_discharge: string | null
  vat_deferred: boolean
  use_cyuk_dan: boolean
  eori: string | null
  vat_no: string | null
  // Z98: set at save time only -- the invoice's own totals, before they are replaced by the sum of the 2 dp lines
  stated_total_amount?: number | null
  stated_gross_weight_kg?: number | null
  stated_net_weight_kg?: number | null
}

// A mapped cell's raw text sometimes carries a fixed label alongside the
// value (e.g. "INVOICE NO. : 2026-MF062603") -- strip_before/strip_after let
// template replay recover the clean value instead of the whole cell string.
// Matched case-insensitively; trim defaults to true.
export interface FieldMappingCell {
  row: number
  col: number
  strip_before?: string
  strip_after?: string
  trim?: boolean
}

// Item-table columns have no per-row "row" (that's the loop index at apply
// time) -- just a column index, optionally with the same strip rules.
export type ItemColumnMapping = number | {
  col: number
  strip_before?: string
  strip_after?: string
  trim?: boolean
}

// Row/col positions the AI reported each field at, keyed by field name (header
// fields) plus an 'item_table' entry -- only populated for the Excel path
// (sourceFormat: 'excel'); transient, not persisted on the invoice record --
// callers use it to decide whether to save/update a per-customer template.
export type FieldMappings = Record<string, FieldMappingCell> & {
  item_table?: { start_row: number; end_condition?: string; columns: Record<string, ItemColumnMapping> }
}

export interface InvoiceParsed {
  header: InvoiceHeader
  items: InvoiceItem[]
  pivot: InvoicePivotItem[]
  confidence: number
  field_mappings?: FieldMappings
}

// The AI sometimes returns a whole phrase ("Country of Origin: china") instead of the ISO-2 code
// its prompt asks for. Extract the code; if the text can't be resolved, keep it as-is so the
// review UI flags it red for staff to pick (never silently default) -- see isIsoCountry().
export function normalizeParsedCountries(parsed: InvoiceParsed): InvoiceParsed {
  const fix = (v: string | null | undefined) => (v ? countryNameToIso2(v) ?? v : v ?? null)
  if (parsed.header) {
    parsed.header.country_of_origin = fix(parsed.header.country_of_origin)
    parsed.header.destination_country = fix(parsed.header.destination_country)
  }
  for (const item of parsed.items || []) item.country_of_origin = fix(item.country_of_origin)
  return parsed
}

export async function parseInvoiceWithClaude(
  rawText: string,
  customerContext?: { importer_name?: string; eori_number?: string },
  sourceFormat: 'excel' | 'pdf' = 'pdf'
): Promise<InvoiceParsed> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!supabaseUrl) throw new Error('Missing VITE_SUPABASE_URL')
  if (!anonKey) throw new Error('Missing VITE_SUPABASE_ANON_KEY')

  console.log(`[claudeInvoiceParser] Sending rawText: ${rawText.length} chars` + (customerContext ? ` (customer: ${customerContext.importer_name})` : ''))

  const response = await fetch(`${supabaseUrl}/functions/v1/parse-invoice`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${anonKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rawText, customerContext, sourceFormat })
  })

  const responseData = await response.json()
  if (!response.ok) {
    const errorMsg = responseData?.error || `HTTP ${response.status}`
    console.error(`[claudeInvoiceParser] Parse error: ${errorMsg}`)
    throw new Error(`Parse failed: ${errorMsg}`)
  }

  // parse-invoice sets this when it had to recover a truncated response (some
  // trailing fields, often field_mappings, may be missing) or fell back to an
  // empty result entirely -- surfacing it here means a failure is diagnosable
  // from the browser console alone, without a separate Supabase logs query.
  if ((responseData as any)._parse_warning) {
    console.warn(`[claudeInvoiceParser] ${(responseData as any)._parse_warning}`, responseData)
  }

  console.log(`[claudeInvoiceParser] Parse successful: ${responseData.items?.length || 0} items`)
  return normalizeParsedCountries(responseData as InvoiceParsed)
}

// Hybrid template validation: a stale/mis-anchored template can fail sanity
// checks on just one or two header fields while everything else it read is
// fine -- re-parsing the whole document would throw away good data and cost
// far more tokens, so this asks the AI for a single targeted field instead.
export async function parseSingleFieldWithClaude(
  targetField: string,
  rawText: string,
  sourceFormat: 'excel' | 'pdf' = 'excel'
): Promise<unknown> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!supabaseUrl) throw new Error('Missing VITE_SUPABASE_URL')
  if (!anonKey) throw new Error('Missing VITE_SUPABASE_ANON_KEY')

  const response = await fetch(`${supabaseUrl}/functions/v1/parse-invoice`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${anonKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rawText, sourceFormat, targetField })
  })

  const data = await response.json()
  if (!response.ok) {
    throw new Error(`Single-field parse failed (${targetField}): ${data?.error || `HTTP ${response.status}`}`)
  }
  return data.value ?? null
}
